import React from 'react';
import useFormValues from '../modals/utils/use-form-values';
import { useTranslation } from 'react-i18next';
import { FormikProps, FormikValues, useFormikContext } from 'formik';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import {
  ClassroomNameField,
  OrganizationNameField,
  PrimaryEducatorField,
  SchoolYearField,
  SecretCodeTypeField,
  ClassroomColorField
} from './ClassroomDetailsForm/ClassroomDetailsFields';
import { Organization } from 'types/schema-generated-types';
import DefaultGroupAlert from './GroupAssignmentAlert';

const useStyles = makeStyles(theme =>
  createStyles({
    root: {
      height: '100%'
    },
    header: {
      width: '100%',
      padding: `${theme.spacing(3)}px 0`,
      backgroundColor: theme.palette.secondary.main,
      color: theme.palette.common.white,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center'
    },
    formContainer: {
      marginTop: 0,
      marginBottom: 0,
      width: '100%',
      padding: '24px'
    },
    inputLabel: {
      fontSize: '16px',
      marginBottom: theme.spacing(1)
    },
    input: {
      marginBottom: theme.spacing(3)
    },
    danger: {
      color: 'red'
    }
  })
);

interface CreateClassConfirmationProps {
  disabled?: boolean;
  organization?: Organization;
  organizations?: Array<Organization>;
}

const CreateClassConfirmation = ({ organizations, disabled }: CreateClassConfirmationProps) => {
  const classes = useStyles();
  const { t } = useTranslation('CreateClassConfirmation');
  const { values }: FormikProps<FormikValues> = useFormikContext();
  const [, primaryEducatorFieldValues, , schoolYearFieldValues] = useFormValues(organizations);

  const coeducators = (values && values.CREATE_CLASS && values.CREATE_CLASS.coeducators) || [];

  return values && values.CREATE_CLASS ? (
    <>
      <DefaultGroupAlert />
      <Grid container spacing={5} className={classes.formContainer}>
        <Grid item xs={12} sm={5}>
          <ClassroomNameField
            classes={classes}
            t={t}
            name="CREATE_CLASS.name"
            disabled={disabled}
          />
          <OrganizationNameField
            disabled={disabled}
            classes={classes}
            t={t}
            name="CREATE_CLASS.organization"
            values={organizations?.map(({ name, id }) => ({ label: name, value: id }))}
          />
          <PrimaryEducatorField
            disabled={disabled}
            values={primaryEducatorFieldValues}
            classes={classes}
            name="CREATE_CLASS.educator"
            t={t}
          />
          <SchoolYearField
            disabled={disabled}
            values={schoolYearFieldValues}
            classes={classes}
            name="CREATE_CLASS.schoolYear"
            t={t}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <SecretCodeTypeField
            disabled={disabled}
            classes={classes}
            name="CREATE_CLASS.secretCodeType"
            t={t}
          />
          <ClassroomColorField
            disabled={disabled}
            classes={classes}
            name="CREATE_CLASS.color"
            t={t}
          />
        </Grid>
        <Grid item xs={12} sm={5}>
          <Typography variant="body1">
            <strong>Co-Educators:</strong>
          </Typography>
          <Typography>
            {coeducators &&
              coeducators
                ?.map(
                  ({
                    firstName,
                    lastName,
                    email
                  }: {
                    firstName: string;
                    lastName: string;
                    email: string;
                  }) => `${firstName} ${lastName}(${email})`
                )
                .join(', ')}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={4}></Grid>
      </Grid>
    </>
  ) : (
    <div />
  );
};

export default CreateClassConfirmation;
